import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { BookOpen, Briefcase, Building2, GraduationCap } from 'lucide-react'

import { AuthLayout } from '@/routes/login'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/field'
import { useToast } from '@/components/ui/toast'
import { readSessionAfterLogin, useLogin, useRegister } from '@/features/auth/api'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/cn'
import type { Role } from '@/lib/types'

const schema = z.object({
  full_name: z.string().min(2, 'Nama minimal 2 karakter.').max(120, 'Nama terlalu panjang.'),
  email: z.string().min(1, 'Email wajib diisi.').email('Format email belum benar.'),
  password: z
    .string()
    .min(8, 'Kata sandi minimal 8 karakter.')
    .max(128, 'Kata sandi terlalu panjang.'),
})

type FormValues = z.infer<typeof schema>

const roleOptions: Array<{ value: Role; label: string; description: string; icon: React.ReactNode }> = [
  {
    value: 'student',
    label: 'Peserta',
    description: 'Ikut kelas',
    icon: <GraduationCap className="size-5" />,
  },
  {
    value: 'expert',
    label: 'Tenaga Ahli',
    description: 'Jual jasa & kelas',
    icon: <BookOpen className="size-5" />,
  },
  {
    value: 'client',
    label: 'Klien',
    description: 'Pesan pekerjaan',
    icon: <Briefcase className="size-5" />,
  },
  {
    value: 'employer',
    label: 'Perekrut',
    description: 'Pasang lowongan',
    icon: <Building2 className="size-5" />,
  },
]

export function RegisterPage() {
  const [roles, setRoles] = useState<Role[]>(['student'])
  const register = useRegister()
  const login = useLogin()
  const navigate = useNavigate()
  const toast = useToast()
  const queryClient = useQueryClient()

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { full_name: '', email: '', password: '' },
  })

  const toggleRole = (role: Role) => {
    setRoles((current) => {
      if (current.includes(role)) {
        // Selalu sisakan minimal satu peran; akun tanpa peran tidak dapat
        // melakukan apa pun setelah masuk.
        return current.length === 1 ? current : current.filter((item) => item !== role)
      }
      return [...current, role]
    })
  }

  const submit = form.handleSubmit((values) => {
    register.mutate(
      { ...values, roles },
      {
        onSuccess: () => {
          // Pendaftaran langsung dilanjutkan dengan masuk supaya pengguna
          // tidak perlu mengetik kredensial yang sama dua kali.
          login.mutate(
            { email: values.email, password: values.password },
            {
              onSuccess: async () => {
                // Sama seperti pada halaman masuk: cookie sesi tidak dapat
                // dibaca JavaScript, jadi keberhasilannya dipastikan dengan
                // bertanya balik ke server.
                if (!(await readSessionAfterLogin(queryClient))) {
                  toast.error(
                    'Sesi tidak tersimpan',
                    'Akun Anda sudah dibuat, tetapi peramban ini menolak menyimpan cookie sesi. ' +
                      'Coba buka skava.id langsung di Chrome atau Safari, lalu masuk dengan akun tadi.',
                  )
                  return
                }
                await navigate({ to: '/app' })
              },
              onError: () => navigate({ to: '/masuk' }),
            },
          )
        },
        onError: (error) => {
          if (error instanceof ApiError) {
            const fields = error.fieldErrors
            for (const [field, message] of Object.entries(fields)) {
              if (field in form.getValues()) {
                form.setError(field as keyof FormValues, { message })
              }
            }
            toast.error('Pendaftaran gagal', error.message)
            return
          }
          toast.error('Pendaftaran gagal', 'Periksa koneksi Anda lalu coba lagi.')
        },
      },
    )
  })

  return (
    <AuthLayout title="Buat akun Skava" subtitle="Satu akun untuk belajar, bekerja, dan merekrut.">
      <form onSubmit={submit} className="space-y-3.5" noValidate>
        <Input
          label="Nama lengkap"
          autoComplete="name"
          placeholder="Nama sesuai identitas"
          error={form.formState.errors.full_name?.message}
          {...form.register('full_name')}
        />

        <Input
          label="Email"
          type="email"
          autoComplete="email"
          placeholder="nama@perusahaan.id"
          error={form.formState.errors.email?.message}
          {...form.register('email')}
        />

        <Input
          label="Kata sandi"
          type="password"
          autoComplete="new-password"
          placeholder="Minimal 8 karakter"
          error={form.formState.errors.password?.message}
          {...form.register('password')}
        />

        <fieldset>
          <legend className="mono-label text-[11px] text-subtle">
            Saya ingin · bisa lebih dari satu
          </legend>

          <div className="mt-3 grid grid-cols-2 gap-2">
            {roleOptions.map((option) => {
              const active = roles.includes(option.value)
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggleRole(option.value)}
                  aria-pressed={active}
                  className={cn(
                    'flex items-center gap-2.5 rounded-2xl border-2 p-2.5 text-left transition',
                    active ? 'border-brand bg-brand-soft' : 'border-line hover:border-line-strong',
                  )}
                >
                  <span
                    className={cn(
                      'flex size-9 shrink-0 items-center justify-center rounded-xl',
                      active ? 'bg-inverted text-on-inverted' : 'bg-raised text-muted',
                    )}
                  >
                    {option.icon}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-bold text-ink">{option.label}</span>
                    <span className="block truncate text-xs text-muted">{option.description}</span>
                  </span>
                </button>
              )
            })}
          </div>
        </fieldset>

        <Button type="submit" fullWidth size="lg" loading={register.isPending || login.isPending}>
          Buat akun
        </Button>

        <p className="text-center text-xs leading-relaxed text-subtle">
          Dengan mendaftar, Anda menyetujui Syarat &amp; Ketentuan dan Kebijakan Privasi. Persetujuan dicatat
          beserta waktu dan alamat IP sesuai UU Perlindungan Data Pribadi.
        </p>
      </form>

      <p className="mt-5 text-center text-sm text-muted">
        Sudah punya akun?{' '}
        <Link to="/masuk" className="font-semibold text-brand hover:underline">
          Masuk di sini
        </Link>
      </p>
    </AuthLayout>
  )
}
